/**
 * La **boutique** et le matériel de VIE — ⟦A-11 §4⟧ (avenant du 14/08/2026), tâche A-9 arc 3
 * point 6.
 *
 * « La collab fait *entrer* l'argent, la boutique le fait *sortir*. » C'est la contrepartie
 * du Grand Saut : à partir de 1 000 abonnés, le joueur a des Clicks, des charges, et pour la
 * première fois une raison de choisir entre garder et dépenser.
 *
 * ⚠️ **Ce n'est PAS l'échelle de matériel de tournage.** Celle-là existe déjà : c'est la
 * branche Tournage de l'arbre (webcam → ring light → setup → B-roll → multicam → plateau, en
 * **PC + Clicks**, PLAN §252). Ici on n'achète rien pour filmer — on achète de quoi **vivre**,
 * et donc de quoi tenir le rythme : un lit, une chaise, des rideaux, un café.
 *
 * ## Deux natures, deux destins
 *
 *  - **Permanent** — un meuble. Son id entre dans `state.inventaire.permanents` et ses effets
 *    persistants sont **relus depuis le contenu** à chaque résolution par `collectModifiers`,
 *    jamais recopiés dans l'état. Exactement la règle des skills, et pour la même raison :
 *    une seule source de vérité, et un contenu corrigé s'applique rétroactivement.
 *  - **Consommable** — une boisson. Il est **consommé à l'achat** ; son `moment` décide de
 *    l'instant :
 *      · `immediat` → `applyEffectsInPlace` tout de suite (les `gauge_delta` bougent les
 *        jauges, les `action_cost_mult` à `duree_h` sont datés dans l'état et expireront) ;
 *      · `prochaine_nuit` → son id est mis en file dans `state.inventaire.nuit_prochaine`,
 *        et `resoudreNuit` (`sleep.ts`) le consomme au prochain sommeil, une seule fois.
 *
 * ## Ce qui a été TRANCHÉ ici, et qui reste à confirmer par Mathieu
 *
 * La proposition de contenu (`content/boutique.proposition.json`) laissait cinq arbitrages
 * ouverts. Deux touchent le moteur, ils sont tranchés ici **par défaut le plus petit** :
 *
 *  1. **Pas de réserve de consommables en v1.** Acheter un consommable, c'est le consommer.
 *     Une réserve exigerait une seconde action (`utiliser_objet`), un second écran et un
 *     inventaire de comptes — beaucoup de surface pour une décision que le joueur prend de
 *     toute façon au moment où il en a besoin. Si Mathieu veut la réserve, c'est un ajout,
 *     pas une reprise : `nuit_prochaine` montre déjà comment une file d'ids se gère.
 *  2. **Les permanents survivent au respec.** `appliquerRespec` ne touche que `state.skills` :
 *     ce sont des objets, pas des nœuds — le respec rend des PC, pas des meubles. C'est le
 *     comportement obtenu **sans écrire une ligne**, et c'est aussi la proposition du contenu.
 *
 * Non implémenté volontairement : la **revente** (elle ouvrirait une boucle d'exploitation,
 * proposition « non en v1 ») et `overtime_sm_mult`, **BLOQUÉ** tant que le coût de santé
 * mentale du dépassement d'heures n'est pas spécifié — on ne module pas une mécanique qui
 * n'existe pas.
 *
 * Pureté (CLAUDE.md) : aucune I/O, aucun `Math.random`, aucune horloge système, aucun tirage
 * — l'achat est entièrement déterministe.
 */
import { applyEffectsInPlace } from "./effects.js";
import { accorderSurcharge, surchargeDunConsommable } from "./surcharge.js";
import { InvalidAction, } from "./types.js";
/**
 * Extrait les objets d'un contenu moteur qui les porte. Lecture **élargie**, jamais castée en
 * `any` — même patron que `contenuSkills` (`skills.ts`). Contenu sans boutique ⇒ boutique
 * vide, et tout achat échoue en `objet_inconnu` : l'absence est visible dans les tests plutôt
 * que silencieusement permissive.
 */
export function contenuBoutique(content) {
    const porteur = content;
    return { boutique: porteur.boutique ?? [] };
}
// ─────────────────────────────────────────────────────────────────────────────
// L'inventaire
// ─────────────────────────────────────────────────────────────────────────────
/** Inventaire vide — la forme canonique de `state.inventaire`. */
export function inventaireVide() {
    return { permanents: [], nuit_prochaine: [] };
}
/**
 * ⟦A-60⟧ Les consommables **préparés pour la prochaine production**, en lecture tolérante :
 * champ optionnel, `undefined` ⇔ file vide ⇔ état neutre. Lecture seule.
 */
export function prochaineProd(state) {
    return state.inventaire?.prochaine_prod ?? [];
}
/**
 * ⟦A-60⟧ L'objet armé pour la prochaine vidéo, ou `undefined`. Il n'y en a **jamais plus
 * d'un** (`refusAchatObjet`) : c'est ce que l'écran affiche, et c'est aussi la garde qui
 * empêche les consommables de se transformer en progression parallèle.
 */
export function objetPrepare(state, content) {
    const id = prochaineProd(state)[0];
    return id === undefined ? undefined : objetParId(content, id);
}
/**
 * Lecture tolérante de l'inventaire : `state.inventaire` est **optionnel**, `undefined` ⇔
 * inventaire vide (aucun état à migrer). Ne mute pas ; le résultat peut être un objet
 * partagé, donc **lecture seule**.
 */
export function inventaire(state) {
    return state.inventaire ?? inventaireVide();
}
/** Idem, en écriture : matérialise l'inventaire sur l'état (qui doit être un clone). */
function inventaireMutable(state) {
    state.inventaire ??= inventaireVide();
    return state.inventaire;
}
/** L'objet est-il possédé ? (Permanents seulement — un consommable n'est jamais « possédé ».) */
export function objetPossede(state, objetId) {
    return inventaire(state).permanents.includes(objetId);
}
// ─────────────────────────────────────────────────────────────────────────────
// Lectures : objet, palier, prérequis
// ─────────────────────────────────────────────────────────────────────────────
/** L'objet d'id `objetId`, ou `undefined` s'il n'existe pas dans le contenu. */
export function objetParId(content, objetId) {
    return content.boutique.find((o) => o.id === objetId);
}
/**
 * Le palier de l'objet est-il atteint ? Lu comme `exigenceK` (`progress.ts`) : abonnés
 * **courants** OU palier déjà franchi — une chaîne qui redescend sous 10 000 ne perd pas
 * l'accès aux objets qu'elle avait débloqués.
 */
export function palierAtteint(state, objet) {
    return (state.subscribers >= objet.palier_min ||
        state.milestones_reached.includes(objet.palier_min));
}
/** Prérequis de l'objet que le joueur ne possède pas encore (dans l'ordre du contenu). */
export function prerequisManquantsObjet(state, objet) {
    const possedes = new Set(inventaire(state).permanents);
    return objet.prerequis.filter((id) => !possedes.has(id));
}
// ─────────────────────────────────────────────────────────────────────────────
// Validation — une action invalide ne produit AUCUN effet de bord (§2.2)
// ─────────────────────────────────────────────────────────────────────────────
/**
 * Raison de refus d'un achat, **sans lever** : c'est ce que l'UI de la boutique affiche sous
 * un objet grisé. `null` = achat possible. Ordre des vérifications = celui d'`actions.ts` et
 * de `refusAchat` (`skills.ts`) : **structurel d'abord, ressources ensuite** — « il te faut
 * d'abord le matelas » est plus utile que « tu es fauché ».
 */
export function refusAchatObjet(state, objetId, content) {
    const objet = objetParId(content, objetId);
    if (!objet)
        return new InvalidAction("objet_inconnu", { objet_id: objetId });
    if (objet.categorie === "permanent" && objetPossede(state, objet.id)) {
        return new InvalidAction("objet_deja_possede", { objet_id: objet.id });
    }
    if (!palierAtteint(state, objet)) {
        return new InvalidAction("objet_verrouille", {
            objet_id: objet.id,
            palier_min: objet.palier_min,
            subscribers: state.subscribers,
        });
    }
    // ⟦A-60⟧ **Un seul matos armé à la fois.** Ce n'est pas une limite d'inventaire, c'est LA
    // règle qui empêche les consommables de rendre l'arbre inutile : on ne peut jamais armer
    // plus d'une étape par vidéo, donc jamais reconstituer un build à coups d'achats. Le refus
    // est structurel, donc placé avec les autres refus structurels — avant l'argent.
    if (objet.moment === "prochaine_prod" && prochaineProd(state).length > 0) {
        return new InvalidAction("objet_deja_prepare", {
            objet_id: objet.id,
            prepare: prochaineProd(state)[0] ?? null,
        });
    }
    const manquants = prerequisManquantsObjet(state, objet);
    if (manquants.length > 0) {
        return new InvalidAction("objet_prereq_manquant", {
            objet_id: objet.id,
            manquants: manquants.join(","),
            premier_manquant: manquants[0] ?? null,
        });
    }
    if (state.money < objet.prix_clicks) {
        return new InvalidAction("not_enough_money", {
            objet_id: objet.id,
            requis: objet.prix_clicks,
            disponible: state.money,
        });
    }
    return null;
}
/** Idem, mais lève. Rend l'objet validé, pour éviter une seconde recherche. */
export function validateAcheterObjet(state, objetId, content) {
    const refus = refusAchatObjet(state, objetId, content);
    if (refus)
        throw refus;
    return objetParId(content, objetId);
}
/**
 * Applique l'achat d'un objet : Clicks débités, effets appliqués selon la nature de l'objet,
 * inventaire mis à jour. **MUTE `state`** — appelé depuis la file de résolution, qui travaille
 * déjà sur un clone (§2.3). Valide d'abord : appelé seul, il ne peut pas produire d'achat
 * invalide.
 *
 * C'est bien ici que le prix est débité : `actionCost` rend `COUT_NUL` pour `acheter_objet`
 * (§2.2) parce qu'il dépend du contenu — exactement comme pour `acheter_skill`.
 *
 * **Le cas subtil, et pourquoi il est traité ainsi** : un permanent ne doit PAS voir ses
 * effets persistants recopiés dans l'état (`source: "skill"` les écarte), sans quoi ils
 * compteraient deux fois — une fois depuis la trace, une fois depuis `collectModifiers` qui
 * relit le contenu par `inventaire.permanents`. Un consommable, lui, n'est jamais relu : il
 * a besoin de la trace pour que ses temporaires à `duree_h` existent, d'où `"systeme"`.
 */
export function appliquerAchatObjet(state, objetId, content, log) {
    const objet = validateAcheterObjet(state, objetId, content);
    state.money -= objet.prix_clicks;
    const inv = inventaireMutable(state);
    let differe = false;
    if (objet.categorie === "permanent") {
        // Les persistants permanents ne sont volontairement PAS tracés : ils se relisent depuis
        // `content/boutique.json` via `inventaire.permanents` (voir l'en-tête).
        const r = applyEffectsInPlace(state, objet.effets, "skill", { source_id: objet.id });
        inv.permanents.push(objet.id);
        if (log)
            log.push(...r.log);
    }
    else if (objet.moment === "prochaine_nuit") {
        // On ne fait que mettre en file : c'est `resoudreNuit` qui lira les effets, au sommeil.
        inv.nuit_prochaine.push(objet.id);
        differe = true;
    }
    else if (objet.moment === "prochaine_prod") {
        // ⟦A-60⟧ **Le matos de la vidéo qui compte.** Deux temps, et ils ne sont pas au même
        // endroit — c'est ce qui fait de l'objet un objet et non un nœud :
        //  · ses `effets` sont le **prix en jauges**, payé MAINTENANT (préparer coûte : on veille,
        //    on court les magasins). ⟦A-50 §2⟧ « un boost n'est jamais que positif » ;
        //  · ses `assists` attendent en file et arment la **prochaine production**, une seule fois.
        // Pas de `accorderSurcharge` : ces effets-là retirent des jauges, ils n'en remplissent pas.
        const r = applyEffectsInPlace(state, objet.effets, "systeme", { source_id: objet.id });
        (inv.prochaine_prod ??= []).push(objet.id);
        if (log)
            log.push(...r.log);
        differe = true;
    }
    else {
        // ⟦A-20⟧ Une boisson ouvre le plafond AVANT de remplir la jauge : sans ça, boire à 90 ⚡
        // gagnait 10 et jetait le reste — c'est précisément le trou que l'avenant répare.
        accorderSurcharge(state, surchargeDunConsommable(objet.effets));
        const r = applyEffectsInPlace(state, objet.effets, "systeme", { source_id: objet.id });
        if (log)
            log.push(...r.log);
    }
    log?.push({
        turn: state.turn,
        clock_h: state.clock_h,
        text: objet.moment === "prochaine_prod"
            ? `« ${objet.id} » acheté ${objet.prix_clicks} ₵ — armé pour la prochaine vidéo.`
            : differe
                ? `« ${objet.id} » acheté ${objet.prix_clicks} ₵ — ça agira cette nuit.`
                : `« ${objet.id} » acheté ${objet.prix_clicks} ₵.`,
    });
    return {
        objet,
        prix: objet.prix_clicks,
        permanent: objet.categorie === "permanent",
        differe_a_la_nuit: differe,
        arme_pour_la_prod: objet.moment === "prochaine_prod",
    };
}
/**
 * ⟦A-60⟧ **Vide la file `prochaine_prod` et rend ce qui a servi.** MUTE `state`.
 *
 * Appelée par `publier` (et par la vidéo jalon) **après** le calcul des scores effectifs, et
 * jamais avant : le serveur régénère le pattern d'anti-triche depuis l'état d'AVANT l'action
 * (`server/src/routes/actions.ts`), donc l'objet doit encore être en file pendant tout le
 * scoring. Le vider ici, c'est ce qui fait de l'aide une aide d'**une seule** vidéo.
 *
 * Rend `[]` sur une file vide, sans rien écrire : l'état neutre ne crée pas de champ.
 */
export function consommerProchaineProd(state) {
    const file = state.inventaire?.prochaine_prod;
    if (!file || file.length === 0)
        return [];
    const consommes = [...file];
    state.inventaire.prochaine_prod = [];
    return consommes;
}
/**
 * Vide la file `nuit_prochaine` et rend ce qu'elle apporte à CETTE nuit. **MUTE `state`**.
 *
 * Seuls les `sleep_recovery_add` sont lus : c'est le seul type d'effet dont le point
 * d'application EST la nuit (SPEC-MOTEUR §2.4 pas 1). Un consommable de nuit qui porterait un
 * autre type d'effet ne ferait rien — le contenu v1 n'en écrit pas, et l'ignorer vaut mieux
 * que l'appliquer à un instant qui n'est pas le sien.
 *
 * Un id absent du contenu est ignoré sans bruit : il ne peut venir que d'une dérive de
 * contenu, que le boot du serveur refuse déjà.
 */
export function consommerNuitProchaine(state, content) {
    const file = state.inventaire?.nuit_prochaine;
    const total = { energie: 0, sante_mentale: 0 };
    if (!file || file.length === 0)
        return total;
    for (const id of file) {
        const objet = objetParId(content, id);
        if (!objet)
            continue;
        // ⟦A-20⟧ Même règle que pour une boisson `immediat` : le consommable ouvre le plafond,
        // puis le remplit. Les PERMANENTS n'appellent jamais ceci (ils sont relus depuis
        // `inventaire.permanents` par `sleep.ts`) — un bon lit fait mieux récupérer, il ne rend
        // pas surhumain.
        accorderSurcharge(state, surchargeDunConsommable(objet.effets));
        for (const effet of objet.effets) {
            if (effet.type !== "sleep_recovery_add")
                continue;
            total[effet.gauge] += effet.value;
        }
    }
    state.inventaire.nuit_prochaine = [];
    return total;
}
// ─────────────────────────────────────────────────────────────────────────────
// Le hook de résolution (§2.3 pas 6)
// ─────────────────────────────────────────────────────────────────────────────
/**
 * Hook `effetAction` de `applyAction` pour `acheter_objet`. Rend `true` quand l'action a été
 * traitée. Une action invalide lève depuis ici — `applyAction` travaille sur un clone qu'il
 * ne rend jamais, donc l'état d'entrée reste intact (ni Clicks, ni turn).
 */
export function effetActionBoutique(ctx, action) {
    if (action.type !== "acheter_objet")
        return false;
    appliquerAchatObjet(ctx.state, action.objet_id, contenuBoutique(ctx.content), ctx.log);
    return true;
}
/** Jeu de hooks de la boutique, à fusionner par `index.ts` avec ceux des autres modules. */
export const HOOKS_BOUTIQUE = { effetAction: effetActionBoutique };
//# sourceMappingURL=boutique.js.map